import * as React from "react";
import { useState, useRef, useEffect } from "react";
import displayGlobalMessage from "Utils/DisplayGlobalMessage";
import {
    Tab,
    Row,
    Col,
    Nav,
    Form,
    Button,
    Alert,
    InputGroup,
    Spinner,
    Container
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FaRegEyeSlash, FaRegEye } from 'react-icons/fa';

// Assets
import "../Assets/LoginPage.sass";
import "../Assets/custom_modal.css";

// Actions
import {
    authLogin,
    authSignUp,
    requestCompleted,
    authSuccess,
    IITBSSOLogin,
    googleLogin,
} from "Store/UserInfo/Actions";
import { Link, useNavigate, useLocation } from "react-router-dom";
import log from "loglevel";
import { enableSignupConfirmation, enableSignupRollNo, signupRollNoLabel } from "Constants";

const ALLOW_LOGIN_ONLY = process.env.REACT_APP_ALLOW_LOGIN_ONLY === 'true';

function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            // Does this cookie string begin with the name we want?
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

const LoginPage = (props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const userInfo = useSelector((state) => state.userInfo);

    // Check if current URL is /login
    const isLoginRoute = location.pathname === '/login';

    // State
    const [showPassword, setShowPassword] = useState(false);
    const [showSignUpConfirmationModal, setShowSignUpConfirmationModal] = useState(false);
    const [confirmationModalData, setConfirmationModalData] = useState(null);

    // Refs for form inputs
    const firstNameRef = useRef();
    const lastNameRef = useRef();
    const emailRef = useRef();
    const passwordRef = useRef();
    const confirmPasswordRef = useRef();
    const rollNoRef = useRef();
    const loginUsernameRef = useRef();
    const loginPasswordRef = useRef();

    // Actions
    const actions = {
        authLogin,
        ...(ALLOW_LOGIN_ONLY ? {} : {
            authSignUp: (email, firstName, lastName, password, rollNo) =>
                dispatch(authSignUp(email, firstName, lastName, password, rollNo))
        }),
        requestCompleted: (msg, err) => dispatch(requestCompleted(msg, err)),
        IITBSSOLogin,
        googleLogin,
    };

    // Effects
    useEffect(() => {
        const { error } = userInfo;
        log.log("ERROR", error);
        actions.requestCompleted();
    }, []);

    useEffect(() => {
        if (showSignUpConfirmationModal && confirmationModalData) {
            if (firstNameRef.current) firstNameRef.current.value = confirmationModalData?.firstName || "";
            if (lastNameRef.current) lastNameRef.current.value = confirmationModalData?.lastName || "";
            if (emailRef.current) emailRef.current.value = confirmationModalData?.email || "";
            if (passwordRef.current) passwordRef.current.value = confirmationModalData?.password || "";
            if (confirmPasswordRef.current) confirmPasswordRef.current.value = confirmationModalData?.password || "";
            if (enableSignupRollNo && rollNoRef.current) {
                rollNoRef.current.value = confirmationModalData?.rollNo || "";
            }
        }
    }, [showSignUpConfirmationModal, confirmationModalData]);

    useEffect(() => {
        if (userInfo?.msg?.includes?.("Successfully registered.")) {
            if (firstNameRef.current) firstNameRef.current.value = "";
            if (lastNameRef.current) lastNameRef.current.value = "";
            if (emailRef.current) emailRef.current.value = "";
            if (passwordRef.current) passwordRef.current.value = "";
            if (confirmPasswordRef.current) confirmPasswordRef.current.value = "";
            if (enableSignupRollNo && rollNoRef.current) {
                rollNoRef.current.value = "";
            }
        }
    }, [userInfo?.msg]);

    // Handler functions
    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };

    const handleResetPasswordClick = () => {
        if (props.handleHideLogin) {
            props.handleHideLogin();
        }
        navigate('/resetpassword');
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        log.log({ handleSignUp: "Processing sign up" });

        const firstName = firstNameRef.current.value.trim();
        const lastName = lastNameRef.current.value.trim();
        const email = emailRef.current.value.trim();
        const password = passwordRef.current.value.trim();
        const confirmPassword = confirmPasswordRef.current.value.trim();
        const rollNo = rollNoRef.current ? rollNoRef.current.value.trim() : "";

        let error = null;
        let validationSuccessful = false;

        log.log({ handleSignUp: { firstName, lastName, email, password, confirmPassword, error } });

        if (firstName === "") {
            error = "Please enter valid firstname";
            displayGlobalMessage(error, "error");
        } else if (lastName === "") {
            error = "Please enter valid lastname";
            displayGlobalMessage(error, "error");
        } else if (email === "") {
            error = "Please enter valid email";
            displayGlobalMessage(error, "error");
        } else if (password === "") {
            error = "Please enter valid password";
            displayGlobalMessage(error, "error");
        } else if (password.length < 8) {
            error = "Password must be 8 or more characters long";
            displayGlobalMessage(error, "error");
        } else if (enableSignupRollNo && rollNo === "") {
            error = "Please enter valid " + signupRollNoLabel;
            displayGlobalMessage(error, "error");
        } else if (!(/([A-Z]+)/g.test(password))) {
            error = "Password must have at least 1 capital letter";
            displayGlobalMessage(error, "error");
        } else if (!(password.match(/[\W_]/))) {
            error = "Password must have at least 1 special character";
            displayGlobalMessage(error, "error");
        } else if (confirmPassword === "") {
            error = "Please re-enter your password in ConfirmPassword field";
            displayGlobalMessage(error, "error");
        } else if (password !== confirmPassword) {
            error = "Passwords don't match";
            displayGlobalMessage(error, "error");
        } else {
            validationSuccessful = true;
        }

        if (validationSuccessful) {
            await finishSignUp({ email, firstName, lastName, password, rollNo, takeConfirmation: enableSignupConfirmation });
        } else {
            log.log({ handleSignUp: { error } });
            actions.requestCompleted(null, error);
        }
    };

    const editSignUpDetails = () => {
        setShowSignUpConfirmationModal(false);
    };

    const finishSignUp = async ({ email, firstName, lastName, password, rollNo, takeConfirmation = false }) => {
        if (!takeConfirmation) {
            setShowSignUpConfirmationModal(false);
            const response = await actions.authSignUp(email, firstName, lastName, password, rollNo);
            log.log({ authSignUp: response });
        } else {
            setConfirmationModalData({ email, firstName, lastName, password, rollNo });
            setShowSignUpConfirmationModal(true);
        }
    };

    const handleIITBSSOLogin = async (e) => {
        e.preventDefault();
        await actions.IITBSSOLogin();
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        const loginUsername = loginUsernameRef.current.value.trim();
        const loginPassword = loginPasswordRef.current.value.trim();
        let error = null;
        let msg = null;

        if (loginUsername !== "root" && loginUsername !== "student" &&
            (loginUsername === "" || !(loginUsername.match(/^[a-zA-Z0-9.\+\_\-]+@(?:[a-zA-Z0-9]+\.)+[A-Za-z]+$/)))) {
            error = "Please enter valid email";
        } else if (loginPassword === "") {
            error = "Please enter valid password.";
        } else {
            const csrfToken = getCookie('csrftoken');
            log.log({ csrfToken });
            const response = await dispatch(actions.authLogin(loginUsername, loginPassword, csrfToken));
            log.log(" ===> ", response);

            if (response && response.status && response.status === 200) {
                msg = response.data.msg;
                actions.requestCompleted(msg, error);
                setTimeout(() => window.location.href = window.location.origin + "/courseware/courseslist", 300);
            } else if (response && response.status) {
                error = response.data;
                if (error === 'Forbidden 403') {
                    error = "User is not registered.";
                } else if (error.length > 50) {
                    error = "Something went wrong";
                }
            } else {
                error = response && response.data;
            }
        }
        log.log({ loginPassword, loginUsername, error, msg });
        actions.requestCompleted(msg, error);
    };

    // Google Login Methods
    const responseOnSuccess = (response) => {
        log.log("GOOGLE RESPONSE on Success");
        log.log(response);
        actions.googleLogin(response.tokenId);
    };

    const responseOnFailure = (response) => {
        log.log("GOOGLE RESPONSE on Failure");
        log.log(response);
    };

    const cleanMessage = (msg, type) => {
        log.log({ msg, type });
        if (typeof msg === "object") {
            if (type === "success") {
                return "Successful!";
            } else if (type === "error" && msg?.error) {
                return msg?.error;
            } else {
                return "Something went wrong, please try again!";
            }
        } else {
            return msg;
        }
    };

    // Redirect if authenticated
    if (userInfo?.isAuthenticated) {
        window.location.href = "/courseware";
    }

    const loginContent = (
        <Tab.Container defaultActiveKey="signin">
            <Row>
                <Col>
                    <Alert
                        className={(userInfo?.msg === null || userInfo?.msg === undefined) ? "d-none" : ""}
                        variant={"success"}
                    >
                        {cleanMessage(userInfo.msg, "success")}
                    </Alert>
                    <Alert
                        className={(userInfo?.error === null || userInfo?.error === undefined) ? "d-none" : ""}
                        variant={"danger"}
                    >
                        {cleanMessage(userInfo.error, "error")}
                    </Alert>
                    <Container fluid>
                        <Row>
                            <Col>
                                <Nav
                                    variant="tabs"
                                    className="d-flex flex-row justify-content-between w-100"
                                >
                                    <Nav.Item className="d-flex" style={{ flexGrow: 1 }}>
                                        <Nav.Link eventKey="signin" className="w-100 text-center">
                                            Sign In
                                        </Nav.Link>
                                    </Nav.Item>
                                    {!ALLOW_LOGIN_ONLY && (
                                        <Nav.Item className="d-flex" style={{ flexGrow: 1 }}>
                                            <Nav.Link eventKey="Register" className="w-100 text-center">
                                                Register
                                            </Nav.Link>
                                        </Nav.Item>
                                    )}
                                </Nav>
                            </Col>
                            <Col sm={12}>
                                <Tab.Content className="tab-content-responsive">
                                    <Tab.Pane eventKey="signin">
                                        <p className="text-center my-3" style={{ color: "gray" }}>
                                            Sign In to your Bodhitree Account
                                        </p>
                                        <Form>
                                            <Form.Group controlId="emailID">
                                                <Form.Control
                                                    ref={loginUsernameRef}
                                                    type="email"
                                                    placeholder="Email"
                                                    className="my-3"
                                                    autoComplete="username"
                                                />
                                            </Form.Group>

                                            <Form.Group controlId="passwordID">
                                                <InputGroup>
                                                    <Form.Control
                                                        ref={loginPasswordRef}
                                                        type={showPassword ? "text" : "password"}
                                                        placeholder="Password"
                                                        autoComplete="current-password"
                                                    />
                                                    <Button variant="outline-secondary" onClick={togglePasswordVisibility}>
                                                        {showPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                                                    </Button>
                                                </InputGroup>
                                            </Form.Group>
                                            <div className="text-right my-2">
                                                <Button
                                                    variant="link"
                                                    onClick={handleResetPasswordClick}
                                                    className="my-3 modalDialog2 p-0"
                                                    style={{ textDecoration: 'none', border: 'none', background: 'none' }}
                                                >
                                                    Change/Forgot Password?
                                                </Button>
                                            </div>

                                            <p className="text-center" style={{ color: "gray" }}>
                                                By logging in, you agree to our <Link to="/terms">Terms of Use</Link> and
                                                our <Link to={`/privacy`}>Privacy Policy</Link>.
                                            </p>
                                            {userInfo.loading ? (
                                                <Button
                                                    variant="primary"
                                                    className="btn btn-block btn-lg my-3 gap-3"
                                                    disabled={userInfo.loading}
                                                >
                                                    <><Spinner size="sm" className="pr-2" animation="border" /> Signing In ...</>
                                                </Button>
                                            ) : (
                                                <>
                                                    <Button
                                                        variant="primary"
                                                        type="submit"
                                                        className="btn btn-block btn-lg my-3"
                                                        onClick={handleLogin}
                                                        id="signIn"
                                                    >
                                                        Sign In
                                                    </Button>

                                                </>)}

                                            <div className="d-flex flex-column text-center">
                                                <a
                                                    href="https://docs.google.com/document/d/10aLkiROkmA7a1LwesC7cCcCbB0FhLueOSzqQExws6KM/edit?usp=sharing"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="my-3"
                                                >
                                                    Have trouble logging in? See login FAQ
                                                </a>
                                            </div>
                                        </Form>
                                    </Tab.Pane>
                                    {!ALLOW_LOGIN_ONLY && (
                                        <Tab.Pane eventKey="Register">
                                            {showSignUpConfirmationModal ? (
                                                <>
                                                    <p className="text-center mb-4" style={{ color: "gray" }}>
                                                        Please confirm your details once
                                                    </p>
                                                    <div className="d-flex flex-column align-items-start gap-2 w-100">
                                                        <p className="mb-0"><strong>First Name:</strong> {confirmationModalData?.firstName}</p>
                                                        <p className="mb-0"><strong>Last Name:</strong> {confirmationModalData?.lastName}</p>
                                                        <p className="mb-0"><strong>Email:</strong> {confirmationModalData?.email}</p>
                                                        {enableSignupRollNo && <p className="mb-0"><strong>{signupRollNoLabel}:</strong> {confirmationModalData?.rollNo}</p>}
                                                        <p className="text-center mb-0" style={{ color: "gray" }}><em>password is intentionally kept hidden</em></p>
                                                    </div>
                                                    <div className="d-flex flex-row justify-content-center gap-2 mt-4">
                                                        {userInfo.loading ? (
                                                            <Button
                                                                variant="primary"
                                                                className="btn btn-block btn-lg my-3 gap-3"
                                                                disabled={userInfo.loading}
                                                            >
                                                                <><Spinner size="sm" className="pr-2" animation="border" /> Signing up ...</>
                                                            </Button>
                                                        ) : (
                                                            <>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    onClick={editSignUpDetails}
                                                                >
                                                                    Edit Details
                                                                </Button>
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={() => finishSignUp({
                                                                        email: confirmationModalData?.email,
                                                                        firstName: confirmationModalData?.firstName,
                                                                        lastName: confirmationModalData?.lastName,
                                                                        password: confirmationModalData?.password,
                                                                        rollNo: confirmationModalData?.rollNo,
                                                                        takeConfirmation: false
                                                                    })}
                                                                >
                                                                    Confirm
                                                                </Button>
                                                            </>
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <p className="text-center my-3" style={{ color: "gray" }}>
                                                        Create Your Bodhitree Account
                                                    </p>
                                                    <Form>
                                                        <Row>
                                                            <Col xs={12} md={6}>
                                                                <Form.Group controlId="formGridEmail" className="mb-0 pb-0">
                                                                    <Form.Control
                                                                        ref={firstNameRef}
                                                                        required
                                                                        type="text"
                                                                        placeholder="First Name"
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                            <Col xs={12} md={6}>
                                                                <Form.Group controlId="formGridLastName" className="mb-0 pt-0">
                                                                    <Form.Control
                                                                        ref={lastNameRef}
                                                                        required
                                                                        type="text"
                                                                        placeholder="Last Name"
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>

                                                        <Row>
                                                            <Col>
                                                                <Form.Group controlId="formBasicEmail" className="mb-0">
                                                                    <Form.Control
                                                                        ref={emailRef}
                                                                        required
                                                                        type="email"
                                                                        placeholder="Email"
                                                                        autoComplete="username"
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>

                                                        {enableSignupRollNo && (
                                                            <Row>
                                                                <Col>
                                                                    <Form.Group controlId="formBasicRoll" className="mb-0">
                                                                        <Form.Control
                                                                            ref={rollNoRef}
                                                                            required
                                                                            type="text"
                                                                            placeholder={signupRollNoLabel}
                                                                        />
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>
                                                        )}

                                                        <Row>
                                                            <Col xs={12} md={6}>
                                                                <Form.Group controlId="formGridPassword" className="mb-0">
                                                                    <Form.Control
                                                                        ref={passwordRef}
                                                                        required
                                                                        type="password"
                                                                        placeholder="Password"
                                                                        autoComplete="new-password"
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                            <Col xs={12} md={6}>
                                                                <Form.Group controlId="formGridConfirmPassword" className="mb-0">
                                                                    <Form.Control
                                                                        ref={confirmPasswordRef}
                                                                        required
                                                                        type="password"
                                                                        placeholder="Confirm Password"
                                                                        autoComplete="new-password"
                                                                    />
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>
                                                        <p className="text-center text-muted" style={{ fontSize: 12 }}>
                                                            Password Criteria: <br />
                                                            is at least 8 characters long,
                                                            has at least 1 capital letter and
                                                            has at least 1 special character
                                                        </p>
                                                        <p className="text-center" style={{ color: "gray" }}>
                                                            By signing up, you agree to our <Link to="/terms">Terms of Use</Link> and
                                                            our <Link to={`/privacy`}>Privacy Policy</Link>.
                                                        </p>
                                                        <Button
                                                            variant="primary"
                                                            type="submit"
                                                            className="btn btn-block btn-lg my-3"
                                                            onClick={handleSignUp}
                                                            disabled={userInfo.loading}
                                                        >
                                                            {userInfo.loading ? (
                                                                <><Spinner size="sm" className="pr-2" animation="border" /> Signing up ...</>
                                                            ) : (
                                                                "Sign Up"
                                                            )}
                                                        </Button>
                                                    </Form>
                                                </>
                                            )}
                                        </Tab.Pane>
                                    )}
                                </Tab.Content>
                            </Col>
                        </Row>
                    </Container>
                </Col>
            </Row>
        </Tab.Container>
    );

    // Conditional rendering based on URL
    if (isLoginRoute) {
        // For /login route, center the content in middle 4 columns
        return (
            <Container fluid>
                <Row className="justify-content-center">
                    <Col xs={12} sm={10} md={8} lg={6} xl={4}>
                        {loginContent}
                    </Col>
                </Row>
            </Container>
        );
    }

    // For other routes, return the original layout
    return loginContent;
};

export default LoginPage;